// Pure-JS port of inventory_model.py's per-family urgency projection + chart
// spec builder, so settings (growth rate, target date, restock plan,
// family-vs-color grouping) can be recomputed instantly in the browser with
// no server round trip. Keep this in lockstep with generate_urgency_chart()
// in inventory_app/inventory_model.py if that ever changes.

export const DAY_MS = 86400000;
const CHART_BUFFER_DAYS = 60; // inventory_model.py's chart_buffer_months=2 * 30
const TARGET_EXTEND_DAYS = 60;

export function parseDate(s) {
  return new Date(`${s}T00:00:00`);
}

export function daysBetween(fromDate, toDate) {
  return Math.round((toDate.getTime() - fromDate.getTime()) / DAY_MS);
}

export function addDays(date, n) {
  return new Date(date.getTime() + n * DAY_MS);
}

export function fmtDate(date) {
  if (!date) return 'beyond range';
  return date.toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric' });
}

export function urgencyColor(days) {
  if (days < 90) return '#DC2626';
  if (days < 180) return '#EA580C';
  if (days < 365) return '#CA8A04';
  return '#16A34A';
}

export function urgencyLabel(days) {
  if (days == null) return 'Unknown';
  if (days < 90) return 'Critical';
  if (days < 180) return 'High';
  if (days < 365) return 'Medium';
  return 'Low';
}

// Where a channel's growth rate came from — see sku_family_growth.py's
// _yoy_growth (own → family pool → category pool → 0%).
export const GROWTH_SOURCE_LABEL = {
  sku: "SKU's own year over year",
  family: 'qualifying SKUs in its family',
  category: 'qualifying SKUs in its category',
  default: 'no history — flat 0%',
};

// Growth is only projected for this long, then demand holds at that level:
// the rate rests on one year of evidence, and compounding it further (+78%/yr
// would be 3.2x by year two) is how overstock happens.
export const GROWTH_HORIZON_DAYS = 365;

// Sell-rate multiplier `days` after as_of for a yearly growth `rate`:
// compounds over the first GROWTH_HORIZON_DAYS, flat after.
export function growthMultiplier(rate, days) {
  return Math.pow(1 + rate, Math.min(days, GROWTH_HORIZON_DAYS) / 365);
}

// Builds a small Plotly.js figure of a family's trailing 12 months of net
// units, stacked by channel. Returns null if there isn't at least a partial
// 12mo window to show.
export function buildGrowthTrendFigure(growthEntry, label) {
  const w12 = growthEntry?.window_12mo;
  if (!w12) return null;
  const ch = c => growthEntry.channels?.[c]?.window_12mo;

  // Out-of-stock months (see sku_family_growth.py) are drawn faded.
  const skipped = new Set(w12.excluded_months || []);
  const opacity = w12.months.map(m => (skipped.has(m) ? 0.3 : 1));
  const hovertext = w12.months.map(m => (skipped.has(m) ? 'mostly out of stock' : ''));
  const traces = [];
  for (const [w, name, color] of [[ch('d2c'), 'D2C', '#93C5FD'], [ch('pos'), 'POS', '#C4B5FD'], [ch('wholesale'), 'B2B', '#FDBA74']]) {
    if (!w) continue;
    traces.push({ type: 'bar', name: `${name} units`, x: w.months, y: w.actual_units,
      marker: { color, opacity }, hovertext });
  }

  return {
    data: traces,
    layout: {
      title: { text: `<b>${label}</b> — net units sold / month`, font: { size: 13 } },
      barmode: 'stack',
      xaxis: { title: '', tickangle: -40 },
      yaxis: { title: 'Units/mo', rangemode: 'tozero' },
      height: 400,
      margin: { l: 55, r: 20, t: 40, b: 60 },
      legend: { orientation: 'h', y: -0.35, font: { size: 10 } },
      plot_bgcolor: 'white', paper_bgcolor: 'white',
      hovermode: 'x unified',
    },
  };
}

export function lightenColor(hex, factor = 0.45) {
  const h = hex.replace('#', '');
  const r = parseInt(h.slice(0, 2), 16), g = parseInt(h.slice(2, 4), 16), b = parseInt(h.slice(4, 6), 16);
  const L = c => Math.round(c + (255 - c) * factor).toString(16).padStart(2, '0').toUpperCase();
  return `#${L(r)}${L(g)}${L(b)}`;
}

// Attaches a precomputed `dateOffset` (days from as_of, or null) to each SKU —
// call this once per data load, not on every recompute.
export function withDateOffsets(skus, asOf) {
  return skus.map(s => ({
    ...s,
    dateOffset: s.date ? daysBetween(asOf, parseDate(s.date)) : null,
  }));
}

// events: array of {sku, date, qty}. Returns Map<sku, Map<dayOffset, qty>>.
export function restockEventsBySku(events, asOf) {
  const bySku = new Map();
  for (const ev of events) {
    const offset = daysBetween(asOf, parseDate(ev.date));
    if (!bySku.has(ev.sku)) bySku.set(ev.sku, new Map());
    const m = bySku.get(ev.sku);
    m.set(offset, (m.get(offset) || 0) + ev.qty);
  }
  return bySku;
}

const EMPTY_MAP = new Map();

// Daily stock projection, offsets 0..rangeLen-1 (offset 0 == as_of).
// demandOn(i) = units/day of demand on day i (see skuForecast.demandOnDay).
function projectStock(stock0, demandOn, restockByOffset, rangeLen) {
  const values = new Array(rangeLen);
  let stock = stock0;
  for (let i = 0; i < rangeLen; i++) {
    const effRate = demandOn(i);
    const restockQty = restockByOffset.get(i);
    if (restockQty) stock += restockQty;
    stock = Math.max(0, stock - effRate * (i === 0 ? 0 : 1));
    values[i] = stock;
  }
  return values;
}

function firstZeroIndex(values) {
  for (let i = 0; i < values.length; i++) if (values[i] <= 0) return i;
  return -1;
}

// Mirrors find_final_runout(): last day stock is genuinely, permanently gone
// — null if it never goes positive, or if it's still positive at range end.
function findFinalRunout(values) {
  let lastPos = -1;
  for (let i = 0; i < values.length; i++) if (values[i] > 0) lastPos = i;
  if (lastPos === -1 || lastPos === values.length - 1) return null;
  return lastPos + 1;
}

/**
 * Computes the static, growth-rate-independent overview row for a group —
 * mirrors InventoryModel.get_family_summary(), which is intentionally NOT
 * affected by the live growth-rate/target-date/restock controls (those only
 * drive the chart itself). This is what the report itself already says about
 * a SKU's runway.
 */
export function groupSummary(groupSkus, label, asOf) {
  const finite = groupSkus.filter(s => s.dateOffset != null);
  const dates = finite.map(s => s.dateOffset);
  const daysToFirst = dates.length ? Math.min(...dates) : null;
  return {
    label,
    skus: groupSkus.length,
    totalStock: groupSkus.reduce((sum, s) => sum + s.stock, 0),
    earliestRunout: dates.length ? fmtDate(addDays(asOf, Math.min(...dates))) : null,
    latestRunout: dates.length ? fmtDate(addDays(asOf, Math.max(...dates))) : null,
    daysToFirst,
    urgency: urgencyLabel(daysToFirst),
  };
}

/**
 * Builds a Plotly.js {data, layout} figure for one group (a family, or a
 * color, depending on groupBy). Faithful port of
 * InventoryModel.generate_urgency_chart() — see that function for the
 * reference implementation and comments on the underlying business logic.
 */
export function buildUrgencyFigure({
  groupSkus, labelField, groupTitle, asOf,
  forecastForSku, bufferDays, targetDateOffset, restockBySku,
}) {
  const finiteOffsets = groupSkus.filter(s => s.dateOffset != null).map(s => s.dateOffset);
  if (finiteOffsets.length === 0) return null;

  let endOffset = Math.max(...finiteOffsets) + CHART_BUFFER_DAYS;
  if (targetDateOffset != null && targetDateOffset + TARGET_EXTEND_DAYS > endOffset) {
    endOffset = targetDateOffset + TARGET_EXTEND_DAYS;
  }
  const rangeLen = endOffset + 1;
  const maxDays  = endOffset;

  const labelCounts = new Map();
  for (const s of groupSkus) labelCounts.set(s[labelField], (labelCounts.get(s[labelField]) || 0) + 1);

  const rows = [];
  for (const s of groupSkus) {
    // { fc: per-channel forecast (skuForecast), demandOn(i): units/day on day i }
    const { fc, demandOn } = forecastForSku(s);
    const baseRate = fc.today;
    let label = s[labelField] || s.sku;
    if (labelCounts.get(s[labelField]) > 1) label = `${label} (${s.sku})`;

    const sNo    = projectStock(s.stock, demandOn, EMPTY_MAP, rangeLen);
    const zNoIdx = firstZeroIndex(sNo);
    const daysNo = zNoIdx === -1 ? maxDays : zNoIdx;
    const dateNo = zNoIdx === -1 ? null : addDays(asOf, zNoIdx);

    const skuRestocks = restockBySku.get(s.sku) || EMPTY_MAP;
    const hasRs = skuRestocks.size > 0;
    const sRs   = hasRs ? projectStock(s.stock, demandOn, skuRestocks, rangeLen) : sNo;

    let gapDays = 0;
    if (hasRs) {
      const fz = firstZeroIndex(sRs);
      if (fz !== -1) {
        let posAfter = -1;
        for (let i = fz + 1; i < sRs.length; i++) if (sRs[i] > 0) { posAfter = i; break; }
        if (posAfter !== -1) gapDays = posAfter - fz;
      }
    }

    const finalOffset   = hasRs ? findFinalRunout(sRs) : (zNoIdx === -1 ? null : zNoIdx);
    const daysRs         = finalOffset != null ? finalOffset : maxDays;
    const dateRs          = finalOffset != null ? addDays(asOf, finalOffset) : null;
    const restockRunway  = hasRs ? Math.max(0, daysRs - daysNo - gapDays) : 0;
    const totalX          = daysNo + gapDays + restockRunway;

    let leverText = null, leverColor = null, leverBg = null, leverBc = null;
    if (targetDateOffset != null) {
      const idx = Math.min(Math.max(targetDateOffset, 0), sRs.length - 1);
      const stockAtTarget = Math.max(0, sRs[idx]);
      if (totalX < targetDateOffset) {
        let demand = 0;   // grown demand day by day, runout → target
        for (let i = totalX + 1; i <= targetDateOffset; i++) demand += demandOn(i);
        const unitsNeeded  = Math.ceil(demand);
        leverText  = `  Reorder: ${unitsNeeded.toLocaleString()} units  `;
        leverColor = '#1D4ED8'; leverBg = '#DBEAFE'; leverBc = '#3B82F6';
      } else if (stockAtTarget > 0) {
        const excessUnits = Math.ceil(stockAtTarget);
        const extraPm      = Math.ceil(stockAtTarget / Math.max(1, targetDateOffset / 30.44));
        leverText  = `  ${excessUnits.toLocaleString()} units excess (+${extraPm.toLocaleString()} u/mo)  `;
        leverColor = '#5B21B6'; leverBg = '#EDE9FE'; leverBc = '#7C3AED';
      }
    }

    rows.push({
      label, sku: s.sku, stock: s.stock, rate: baseRate, fc,
      daysNo, dateNo, gapDays, restockRunway, dateRs, hasRs, hasGap: gapDays > 0,
      totalX, leverText, leverColor, leverBg, leverBc,
    });
  }

  if (rows.length === 0) return null;
  rows.sort((a, b) => a.daysNo - b.daysNo);

  const labelsY = rows.map(r => r.label);
  const pct = r => `${r >= 0 ? '+' : ''}${(r * 100).toFixed(1)}%`;
  const pctOr = r => (r == null ? '—' : pct(r));
  const growthText = r => {
    const { d2c, pos, b2b } = r.fc;
    return `${pctOr(r.fc.rate)}/yr combined · D2C ${d2c.today.toFixed(2)}/day ${pct(d2c.rate)} · ` +
           `POS ${pos.today.toFixed(2)}/day ${pct(pos.rate)} · B2B ${b2b.today.toFixed(2)}/day ${pct(b2b.rate)}`;
  };

  const traces = [
    {
      type: 'bar', orientation: 'h', name: '_base', showlegend: false,
      x: rows.map(r => r.daysNo), y: labelsY,
      marker: { color: rows.map(r => urgencyColor(r.daysNo)), line: { width: 0 } },
      customdata: rows.map(r => [r.stock, r.rate, urgencyLabel(r.daysNo), fmtDate(r.dateNo), growthText(r)]),
      hovertemplate:
        '<b>%{y}</b><br>' +
        'Growth: %{customdata[4]}<br>' +
        'Days to runout: %{x:,d}<br>Runout: %{customdata[3]}<br>' +
        'Stock: %{customdata[0]:,.0f} units<br>Rate: %{customdata[1]:.2f} u/day<br>' +
        'Urgency: %{customdata[2]}<extra></extra>',
    },
    {
      type: 'bar', orientation: 'h', name: '_gap', showlegend: false,
      x: rows.map(r => r.gapDays), y: labelsY,
      marker: {
        color: rows.map(r => (r.hasGap ? '#9CA3AF' : 'rgba(0,0,0,0)')),
        pattern: { shape: rows.map(r => (r.hasGap ? '/' : '')), fgcolor: 'rgba(255,255,255,0.45)' },
        line: { width: 0 },
      },
      hovertemplate: '<b>%{y} — out of stock gap</b><br>Gap: %{x:,d} days<extra></extra>',
    },
    {
      type: 'bar', orientation: 'h', name: '_restock', showlegend: false,
      x: rows.map(r => r.restockRunway), y: labelsY,
      marker: {
        color: rows.map(r => (r.hasRs ? lightenColor(urgencyColor(r.daysNo)) : 'rgba(0,0,0,0)')),
        line: { width: 0 },
      },
      customdata: rows.map(r => [fmtDate(r.dateRs)]),
      hovertemplate: '<b>%{y} — restock runway</b><br>Extra days: %{x:,d}<br>Post-restock runout: %{customdata[0]}<extra></extra>',
    },
  ];

  const legendItems = [
    ['#DC2626', 'Critical < 90d'], ['#EA580C', 'High < 180d'],
    ['#CA8A04', 'Medium < 1yr'],   ['#16A34A', 'Low > 1yr'],
    ['#9CA3AF', 'Out of stock gap'], [lightenColor('#16A34A'), 'Restock runway'],
  ];
  for (const [color, lbl] of legendItems) {
    traces.push({
      type: 'scatter', mode: 'markers', x: [null], y: [null],
      marker: { symbol: 'square', size: 13, color }, name: lbl, showlegend: true,
    });
  }

  const maxBar = Math.max(...rows.map(r => r.totalX));
  const annotations = [];
  const YSHIFTS = { 3: [24, 0, -24], 2: [13, -13], 1: [0] };

  for (const r of rows) {
    const n  = 1 + (r.hasRs ? 1 : 0) + (r.leverText ? 1 : 0);
    const ys = YSHIFTS[n];
    let yi = 0;
    annotations.push({
      x: r.totalX, y: r.label,
      text: `  ${r.daysNo.toLocaleString()}d  |  ${fmtDate(r.dateNo)}  |  ${Math.round(r.stock).toLocaleString()} units  |  ${r.rate.toFixed(2)} u/day`,
      showarrow: false, xanchor: 'left', yshift: ys[yi],
      font: { size: 11, color: '#1F2937' }, bgcolor: 'rgba(0,0,0,0)',
    });
    yi++;
    if (r.hasRs) {
      const rsText = r.hasGap
        ? `  ${r.gapDays}d gap  →  out: ${fmtDate(r.dateRs)}`
        : `  +${r.restockRunway.toLocaleString()}d  →  out: ${fmtDate(r.dateRs)}`;
      annotations.push({
        x: r.totalX, y: r.label, text: rsText,
        showarrow: false, xanchor: 'left', yshift: ys[yi],
        font: { size: 11, color: '#713F12' },
        bgcolor: '#FEF08A', bordercolor: '#D97706', borderwidth: 1, borderpad: 3,
      });
      yi++;
    }
    if (r.leverText) {
      annotations.push({
        x: r.totalX, y: r.label, text: r.leverText,
        showarrow: false, xanchor: 'left', yshift: ys[yi],
        font: { size: 11, color: r.leverColor },
        bgcolor: r.leverBg, bordercolor: r.leverBc, borderwidth: 1, borderpad: 3,
      });
    }
  }

  const shapes = [];
  const axisRange = Math.max(maxBar * 1.55, targetDateOffset != null ? targetDateOffset * 1.15 : 0);
  // Top-of-chart labels (target date + 90/180/365d markers) all sit near the same
  // paper y; when the target date lands close to a threshold (e.g. ~365d out) their
  // text collides, so stagger overlapping labels onto higher y-levels.
  const topLevels   = [1.02, 1.09, 1.16, 1.23];
  const minLabelGap = axisRange * 0.06;
  const topMarkers  = [];

  if (targetDateOffset != null) {
    if (bufferDays > 0) {
      shapes.push({
        type: 'rect', x0: targetDateOffset - bufferDays, x1: targetDateOffset + bufferDays,
        y0: 0, y1: 1, yref: 'paper', fillcolor: 'rgba(124,58,237,0.07)', layer: 'below', line: { width: 0 },
      });
    }
    shapes.push({
      type: 'line', x0: targetDateOffset, x1: targetDateOffset, y0: 0, y1: 1, yref: 'paper',
      line: { dash: 'dash', color: 'rgba(124,58,237,0.35)', width: 1.5 },
    });
    const bufStr = bufferDays > 0 ? ` (±${bufferDays}d)` : '';
    topMarkers.push({
      x: targetDateOffset,
      text: `Target: ${addDays(asOf, targetDateOffset).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}${bufStr}`,
      color: '#7C3AED',
    });
  }
  for (const [threshold, lbl, color] of [[90, '90d', '#DC2626'], [180, '180d', '#EA580C'], [365, '1 year', '#CA8A04']]) {
    shapes.push({
      type: 'line', x0: threshold, x1: threshold, y0: 0, y1: 1, yref: 'paper',
      line: { dash: 'dash', color, width: 1.5 }, opacity: 0.55,
    });
    topMarkers.push({ x: threshold, text: lbl, color });
  }

  topMarkers.sort((a, b) => a.x - b.x);
  let prevX = -Infinity;
  let level = 0;
  let maxLevel = 0;
  for (const m of topMarkers) {
    level = (m.x - prevX < minLabelGap) ? level + 1 : 0;
    maxLevel = Math.max(maxLevel, level);
    prevX = m.x;
    annotations.push({
      x: m.x, y: topLevels[Math.min(level, topLevels.length - 1)], yref: 'paper', text: m.text,
      showarrow: false, xanchor: 'center', yanchor: 'bottom', font: { size: 10, color: m.color },
    });
  }

  const maxLines  = Math.max(...rows.map(r => 1 + (r.hasRs ? 1 : 0) + (r.leverText ? 1 : 0)));
  const pxPerRow  = { 1: 80, 2: 110, 3: 140 }[Math.min(maxLines, 3)];
  const chartH    = Math.max(650, rows.length * pxPerRow + 240);

  const layout = {
    barmode: 'stack',
    title: {
      text: `<b>${groupTitle} — Days of Stock Remaining (Average Case)</b><br>` +
            `<sup>Sell rate grows at each SKU's own 12mo rate (hover for detail) · As of ${asOf.toISOString().slice(0, 10)}</sup>`,
      x: 0.02, font: { size: 15 },
    },
    xaxis: {
      title: 'Days remaining', showgrid: true, gridcolor: '#F3F4F6',
      range: [0, axisRange],
    },
    yaxis: { title: '', automargin: true },
    legend: {
      orientation: 'h', yref: 'container', yanchor: 'bottom', y: 24 / chartH, xanchor: 'center', x: 0.5,
      font: { size: 11 }, bgcolor: 'rgba(255,255,255,0.9)', bordercolor: '#D1D5DB', borderwidth: 1,
    },
    plot_bgcolor: 'white', paper_bgcolor: 'white',
    height: chartH, hovermode: 'closest',
    margin: { l: 20, r: 30, t: 155 + maxLevel * 16, b: 140 },
    shapes, annotations,
  };

  return { data: traces, layout };
}
